import { useEffect, useState } from "react";

import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ModalContent } from "../components/ModalContent";

import { Button } from "@/components/ui/button";

import {
  Loader2,
  Package,
  IndianRupee,
  Layers3,
  FileText,
  Hash,
  Percent,
  CalendarDays,
  Tag,
} from "lucide-react";

import { useDispatch } from "react-redux";

import { showToast } from "@/redux/Toast/actions";
import { callLoader } from "@/redux/Loader/actions";

import {
  insertBaseModule,
  updateBaseModule,
} from "@/redux/Modules/baseModules/actions";
import ImageUploadField from "@/components/common/ImageUploadField";
import { Input, SelectInput } from "../components/FormControls";

const Toggle = ({ checked, onChange, label }) => {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 transition-all ${
        checked
          ? "border-emerald-500 bg-emerald-500/10"
          : "border-slate-200 dark:border-slate-700"
      }`}
    >
      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
        {label}
      </span>

      <div
        className={`relative h-6 w-11 rounded-full transition-colors ${
          checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
        }`}
      >
        <div
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </div>
    </button>
  );
};

const initialState = {
  MODULE_NAME: "",
  MODULE_DESCRIPTION: "",
  MODULE_TYPE: "",
  MODULE_BASEPRICE: 0,
  MODULE_BASEPRICETYPE: "PER MONTH",
  MODULE_PERSEATPRICE: 0,
  MODULE_USAGERATE: 0,
  MODULE_PRICINGMODEL: "",
  TRIAL_DAYS: 0,
  SEQUENCE_NO: 0,
  CREATED_BY: "admin",
  BASEMODULE_ID: 0,
  FILEDATA: null,
};

export default function AddBaseModuleModal({
  open,
  onOpenChange,
  onSuccess,
  isEdit = false,
  editData = null,
}) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState(initialState);

  const [errors, setErrors] = useState({});

  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    if (!open) {
      setFormData(initialState);
      setErrors({});
      setPreviewImage(null);
      return;
    }

    // EDIT MODE
    if (isEdit && editData) {
      setFormData({
        MODULE_NAME: editData?.BASEMODULE_NAME || "",
        MODULE_DESCRIPTION: editData?.BASEMODULE_DESCRIPTION || "",
        MODULE_TYPE: editData?.BASEMODULE_TYPE || "",
        MODULE_BASEPRICE: editData?.BASEMODULE_BASEPRICE || 0,
        MODULE_BASEPRICETYPE: editData?.BASEMODULE_BASEPRICETYPE || "PER MONTH",
        MODULE_PERSEATPRICE: editData?.BASEMODULE_PERSEATPRICE || 0,
        MODULE_USAGERATE: editData?.BASEMODULE_USAGERATE || 0,
        MODULE_PRICINGMODEL: editData?.BASEMODULE_PRICINGMODEL || "",
        TRIAL_DAYS: editData?.BASEMODULE_TRIAL_DAYS || 0,
        SEQUENCE_NO: editData?.BASEMODULE_SEQUENCE_NO || 0,
        CREATED_BY: editData?.BASEMODULE_CREATED_BY || "admin",
        BASEMODULE_ID: editData?.BASEMODULE_ID || 0,

        FILEDATA: null,
        MODULE_LOGO: editData?.BASEMODULE_LOGO || "",
      });

      setPreviewImage(editData?.BASEMODULE_LOGO || "");
      return;
    }

    // ADD MODE (important reset)
    setFormData(initialState);
    setErrors({});
    setPreviewImage(null);
  }, [open, isEdit, editData]);

  //   const previewImage = useMemo(() => {
  //     if (!formData.FILEDATA) return null;

  //     return URL.createObjectURL(formData.FILEDATA);
  //   }, [formData.FILEDATA]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.MODULE_NAME?.trim()) {
      nextErrors.MODULE_NAME = "Module name is required";
    }

    if (!formData.MODULE_TYPE?.trim()) {
      nextErrors.MODULE_TYPE = "Module type is required";
    }

    if (!formData.MODULE_BASEPRICETYPE?.trim()) {
      nextErrors.MODULE_BASEPRICETYPE = "Pricing type is required";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    try {
      if (!validate()) return;

      setLoading(true);

      dispatch(callLoader(true));

      const payload = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === "FILEDATA") {
          payload.append(key, value || "");
        } else {
          payload.append(key, value ?? "");
        }
      });

      if (isEdit) {
        dispatch(updateBaseModule(payload));

        dispatch(
          showToast("Base module updated successfully", "success", true),
        );
      } else {
        dispatch(insertBaseModule(payload));

        dispatch(
          showToast("Base module created successfully", "success", true),
        );
      }

      onSuccess?.();

      onOpenChange(false);
    } catch (error) {
      console.error(error);

      dispatch(
        showToast(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to create base module",
          "error",
          true,
        ),
      );
    } finally {
      setLoading(false);
      dispatch(callLoader(false));
    }
  };

  console.log("hello", isEdit, editData);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ModalContent className="h-[90vh] flex flex-col">
        {/* HEADER */}

        <DialogHeader className="shrink-0 relative border-b border-white/10 px-6 py-5 bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-teal-500/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-teal-500" />
            </div>

            <div>
              <DialogTitle className="text-xl font-semibold text-white">
                {isEdit ? "Update Base Module" : "Add Base Module"}
              </DialogTitle>

              <DialogDescription className="mt-1 text-sm text-slate-300">
                {isEdit
                  ? "Update module pricing and configuration settings."
                  : "Create a new base module with pricing and configuration settings."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* BODY */}

        <div className="flex-1 grid grid-cols-1 gap-6 overflow-y-auto p-6 lg:grid-cols-2">
          {/* LEFT */}

          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5 space-y-5">
              <div className="flex items-center gap-2">
                <Layers3 className="w-4 h-4 text-teal-500" />

                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Module Information
                </h3>
              </div>

              <Input
                label="Module Name"
                name={"MODULE_NAME"}
                required
                icon={Package}
                placeholder="Enter module name"
                value={formData.MODULE_NAME}
                onValueChange={handleChange}
                error={errors.MODULE_NAME}
              />

              <Input
                label="Description"
                name="MODULE_DESCRIPTION"
                textarea
                icon={FileText}
                placeholder="Enter module description"
                value={formData.MODULE_DESCRIPTION}
                onValueChange={handleChange}
              />

              <SelectInput
                label="Module Type"
                name="MODULE_TYPE"
                icon={Tag}
                value={formData.MODULE_TYPE}
                onValueChange={handleChange}
                error={errors.MODULE_TYPE}
                placeholder="Select Module Type"
                options={[
                  { value: "CORE", label: "CORE" },
                  { value: "ADDON", label: "ADDON" },
                ]}
              />

              <Input
                label="Pricing Model"
                name="MODULE_PRICINGMODEL"
                icon={Layers3}
                placeholder="Subscription / Usage Based"
                value={formData.MODULE_PRICINGMODEL}
                onValueChange={handleChange}
              />
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
              <ImageUploadField
                label="Module Logo"
                value={previewImage}
                required={false}
                maxSize={5}
                accept={[
                  "image/png",
                  "image/jpeg",
                  "image/jpg",
                  "image/webp",
                  "image/svg+xml",
                ]}
                hint="PNG, JPG, WEBP, SVG • Max 5MB"
                onChange={(preview, file) => {
                  setPreviewImage(preview);

                  handleChange("FILEDATA", file);
                }}
              />
            </div>
          </div>

          {/* RIGHT */}

          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5 space-y-5">
              <div className="flex items-center gap-2">
                <IndianRupee className="w-4 h-4 text-emerald-500" />

                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Pricing Configuration
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  label="Base Price"
                  name="MODULE_BASEPRICE"
                  icon={IndianRupee}
                  value={formData.MODULE_BASEPRICE}
                  onValueChange={handleChange}
                />

                <Input
                  label="Price Type"
                  name="MODULE_BASEPRICETYPE"
                  required
                  icon={Tag}
                  placeholder="PER MONTH"
                  value={formData.MODULE_BASEPRICETYPE}
                  onValueChange={handleChange}
                  error={errors.MODULE_BASEPRICETYPE}
                />

                <Input
                  type="number"
                  name="MODULE_PERSEATPRICE"
                  label="Per Seat Price"
                  icon={IndianRupee}
                  value={formData.MODULE_PERSEATPRICE}
                  onValueChange={handleChange}
                />

                <Input
                  type="number"
                  name="MODULE_USAGERATE"
                  label="Usage Rate"
                  icon={Percent}
                  value={formData.MODULE_USAGERATE}
                  onValueChange={handleChange}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5 space-y-5">
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-amber-500" />

                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Additional Settings
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  label="Sequence No"
                  name="SEQUENCE_NO"
                  icon={Hash}
                  value={formData.SEQUENCE_NO}
                  onValueChange={handleChange}
                />

                <Input
                  type="number"
                  name="TRIAL_DAYS"
                  label="Trial Days"
                  icon={CalendarDays}
                  value={formData.TRIAL_DAYS}
                  onValueChange={handleChange}
                />

                {/* <Input
                    type="number"
                    label="Base Module ID"
                    name="BASEMODULE_ID"
                    icon={Hash}
                    value={formData.BASEMODULE_ID}
                    onValueChange={handleChange}
                  /> */}

                <Input
                  label="Created By"
                  name="CREATED_BY"
                  icon={Tag}
                  value={formData.CREATED_BY}
                  onValueChange={handleChange}
                />
              </div>

              <Toggle
                checked={Number(formData.TRIAL_DAYS) > 0}
                onChange={(value) => handleChange("TRIAL_DAYS", value ? 14 : 0)}
                label="Enable Trial Period"
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}

        <DialogFooter className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-slate-50 dark:bg-slate-900/50">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-teal-500 hover:bg-teal-600 text-white min-w-[140px]"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEdit ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <Package className="w-4 h-4 mr-2" />
                {isEdit ? "Update Module" : "Create Module"}
              </>
            )}
          </Button>
        </DialogFooter>
      </ModalContent>
    </Dialog>
  );
}
